import { useCallback, useEffect, useState } from 'react';
import { Card, PageHeader, Stat, StatusBadge } from '@/components/ui';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface ChargeRow {
  tenant_id: number;
  tenant_name: string;
  tenant_slug: string;
  status: string;
  month: string;
  users: number;
  minutes: number;
  price_per_user_cents: number;
  price_per_minute_cents: number;
  users_cost_cents: number;
  minutes_cost_cents: number;
  ai_transcript_minutes: number;
  ai_transcript_cost_cents: number;
  ai_summary_calls: number;
  ai_summary_cost_cents: number;
  total_cents: number;
  currency: string;
  pricing_configured: boolean;
}

interface Overview {
  month: string;
  rows: ChargeRow[];
  totals: {
    users: number; minutes: number; users_cost_cents: number; minutes_cost_cents: number;
    ai_transcript_minutes: number; ai_transcript_cost_cents: number;
    ai_summary_calls: number; ai_summary_cost_cents: number; total_cents: number;
  };
}

/** עלות ה-AI המצרפית (תמלול + סיכום) בשורה. */
function aiCost(r: { ai_transcript_cost_cents: number; ai_summary_cost_cents: number }): number {
  return (r.ai_transcript_cost_cents ?? 0) + (r.ai_summary_cost_cents ?? 0);
}

function money(cents: number, currency = 'ILS'): string {
  return (cents / 100).toLocaleString('he-IL', { style: 'currency', currency, maximumFractionDigits: 2 });
}

/**
 * "How much to bill everyone": per tenant — active users × per-user price and
 * ACTUAL talk minutes × per-minute price (both set on the tenant), for any
 * month, with a grand total.
 */
export function SuperBilling() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await superApi.billingOverview(month));
    } catch {
      toast('שגיאה בטעינת סקירת חיוב', 'danger');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="חיוב לפי שימוש" actions={
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ padding: 8, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
          aria-label="בחירת חודש"
        />
      } />

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
          <Stat label="מרכזיות" value={data.rows.length} />
          <Stat label="משתמשים פעילים" value={data.totals.users.toLocaleString('he-IL')} />
          <Stat label="דקות שיחה בפועל" value={data.totals.minutes.toLocaleString('he-IL')} />
          <Stat label="עלות AI (תמלול+סיכום)" value={money(data.totals.ai_transcript_cost_cents + data.totals.ai_summary_cost_cents)} />
          <Stat label="סה״כ לחיוב" value={money(data.totals.total_cents)} />
        </div>
      )}

      <Card padded={false}>
        {loading ? (
          <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)' }}>טוען…</div>
        ) : !data || data.rows.length === 0 ? (
          <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)' }}>אין מרכזיות.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['מרכזיה', 'סטטוס', 'משתמשים', '₪/משתמש', 'עלות משתמשים', 'דקות בפועל', '₪/דקה', 'עלות דקות', 'עלות AI', 'סה״כ לחיוב'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.tenant_id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }}>
                    <strong>{r.tenant_name}</strong>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.tenant_slug}</div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <StatusBadge tone={r.status === 'active' ? 'ok' : r.status === 'trial' ? 'warn' : 'neutral'} dot size="sm">{r.status}</StatusBadge>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }}>{r.users}</td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">{r.pricing_configured ? money(r.price_per_user_cents, r.currency) : '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">{money(r.users_cost_cents, r.currency)}</td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }}>{r.minutes.toLocaleString('he-IL')}</td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">{r.pricing_configured ? money(r.price_per_minute_cents, r.currency) : '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">{money(r.minutes_cost_cents, r.currency)}</td>
                  <td
                    style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }}
                    dir="ltr"
                    title={`תמלול: ${r.ai_transcript_minutes} דק' · סיכומים: ${r.ai_summary_calls}`}
                  >
                    {aiCost(r) > 0 ? money(aiCost(r), r.currency) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">
                    {r.pricing_configured
                      ? money(r.total_cents, r.currency)
                      : <span style={{ color: 'var(--warn)', fontSize: 'var(--t-xs)', fontWeight: 400 }}>לא הוגדר תמחור</span>}
                  </td>
                </tr>
              ))}
              {data.totals && (
                <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--surface-2)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }} colSpan={2}>סה״כ</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }}>{data.totals.users}</td>
                  <td />
                  <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{money(data.totals.users_cost_cents)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }}>{data.totals.minutes.toLocaleString('he-IL')}</td>
                  <td />
                  <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{money(data.totals.minutes_cost_cents)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{money(data.totals.ai_transcript_cost_cents + data.totals.ai_summary_cost_cents)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800 }} dir="ltr">{money(data.totals.total_cents)}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        דקות = סכימת זמן דיבור בפועל (עיגול דקה כלפי מעלה לכל שיחה). מחירים נקבעים לכל מרכזיה במסך המרכזיות (בהקמה או בעריכה).
      </p>
    </div>
  );
}
